import React from "react";
import {
  LegalLayout,
  LegalH2,
  LegalH3,
  LegalP,
  LegalUl,
  LegalPlaceholder,
  LegalNote
} from "../components/landing/LegalLayout";
import { useLang } from "../i18n/LanguageContext";
import DatenschutzEn from "./en/DatenschutzEn";

const Mail = () => (
  <a
    href="mailto:wasgitshuet.support@liberox.ch"
    className="font-semibold text-terracotta hover:underline"
  >
    wasgitshuet.support@liberox.ch
  </a>
);

export default function Datenschutz() {
  const { lang } = useLang();
  if (lang === "en") return <DatenschutzEn />;
  return (
    <LegalLayout
      testId="page-datenschutz"
      title="Datenschutzerklärung"
      subtitle="Datenschutzerklärung für die App «WasGitsHüt?» gemäss dem Schweizer Datenschutzgesetz (DSG) und der Datenschutzverordnung (DSV) sowie – soweit anwendbar – der EU-Datenschutz-Grundverordnung (DSGVO)."
    >
      <LegalP>Stand dieser Datenschutzerklärung: 18.09.2026</LegalP>
      <LegalP>
        Diese Datenschutzerklärung erklärt, welche Personendaten wir bei der
        Nutzung der App «WasGitsHüt?» sowie der zugehörigen Webseite bearbeiten,
        weshalb wir dies tun und welche Rechte dir zustehen. Wir richten uns
        primär nach dem Schweizer Datenschutzgesetz (DSG) und der
        Datenschutzverordnung (DSV). Für Nutzerinnen und Nutzer innerhalb der
        Europäischen Union berücksichtigen wir zusätzlich die
        EU-Datenschutz-Grundverordnung (DSGVO).
      </LegalP>
      <LegalP>
        Wir bearbeiten grundsätzlich nur so viele Daten wie nötig
        (Datenminimierung) und gestalten unsere Abläufe nach den Grundsätzen
        «Privacy by Design» und «Privacy by Default». Wo immer möglich
        unterscheiden wir zwischen Daten, die technisch notwendig sind, Angaben,
        die du freiwillig machst, und Daten, die für einzelne Funktionen
        benötigt werden.
      </LegalP>

      <LegalH2>1. Verantwortliche Stelle</LegalH2>
      <LegalP>
        Verantwortlich für die Bearbeitung deiner Personendaten ist:
      </LegalP>
      <LegalUl
        items={[
          "Liberox GmbH («WasGitsHüt?» ist eine Marke der Liberox GmbH)",
          "Bodenäckerstrasse 3, 8957 Spreitenbach, Schweiz",
          "UID: CHE-265.395.466",
          "Telefon: 056 561 67 62"
        ]}
      />
      <LegalP>
        E-Mail für Datenschutzanliegen: <Mail />
      </LegalP>

      <LegalH2>2. Geltungsbereich</LegalH2>
      <LegalP>
        Diese Datenschutzerklärung gilt für die App «WasGitsHüt?» (für iOS und
        Android) sowie für unsere Webseite. Massgebend ist in erster Linie das
        Schweizer Recht (DSG/DSV). Soweit die DSGVO auf dich anwendbar ist
        (z. B. weil du dich in der EU befindest), gelten die zusätzlichen
        Regelungen dieser Erklärung, die ausdrücklich auf die DSGVO Bezug nehmen.
      </LegalP>

      <LegalH2>3. Welche Personendaten wir bearbeiten</LegalH2>
      <LegalP>
        Je nach Nutzung der App können wir folgende Kategorien von Personendaten
        bearbeiten:
      </LegalP>
      <LegalH3>a) Technisch notwendige Daten</LegalH3>
      <LegalUl
        items={[
          "Konto- und Anmeldedaten (z. B. E-Mail-Adresse bzw. Kennung aus dem Apple- oder Google-Login)",
          "Wir bearbeiten technische Informationen wie Geräteart, Betriebssystem, App-Version, IP-Adresse, Zeitpunkt des Zugriffs sowie technische Kennungen, soweit dies für den Betrieb, die Sicherheit und die Fehlerbehebung der App erforderlich ist."
        ]}
      />
      <LegalH3>b) Freiwillige Angaben zum Profil</LegalH3>
      <LegalUl
        items={[
          "Anzeigename und Profilangaben",
          "Persönliche Essenspräferenzen sowie bevorzugte Küchen und Gerichte",
          "Ernährungsweisen (z. B. vegetarisch, vegan)",
          "Abneigungen und Vorlieben bei Lebensmitteln",
          "Budgetpräferenzen für Gerichte (in CHF)"
        ]}
      />
      <LegalH3>c) Besonders schützenswerte Angaben</LegalH3>
      <LegalUl
        items={[
          "Allergien und Unverträglichkeiten (siehe Kapitel 8)"
        ]}
      />
      <LegalH3>d) Funktionsbezogene Nutzungsdaten</LegalH3>
      <LegalUl
        items={[
          "Haushalte und Haushaltsmitglieder sowie Einladungen",
          "Abstimmungen über vorgeschlagene Gerichte",
          "Favorisierte und gespeicherte Rezepte",
          "Einkaufslisten",
          "Angaben rund um Abonnemente und In-App-Käufe (siehe Kapitel 11)"
        ]}
      />

      <LegalH2>4. Benutzerkonto und Registrierung</LegalH2>
      <LegalP>
        Für die Nutzung der zentralen Funktionen erstellst du ein Benutzerkonto.
        Dabei bearbeiten wir die für die Anmeldung nötigen Daten (z. B.
        E-Mail-Adresse bzw. eine Kennung aus dem Apple- oder Google-Login).
        Rechtsgrundlage ist die Erfüllung des Nutzungsvertrags; nach DSGVO
        stützt sich dies auf Art. 6 Abs. 1 lit. b.
      </LegalP>

      <LegalH2>5. Apple Login</LegalH2>
      <LegalP>
        Wenn du dich über «Anmelden mit Apple» registrierst, erhalten wir von
        Apple die zur Kontoerstellung nötigen Angaben (z. B. eine Kennung und –
        falls von dir freigegeben – eine E-Mail-Adresse, gegebenenfalls über
        eine anonymisierte Weiterleitungsadresse von Apple). Die Anmeldung
        erfolgt nach den Vorgaben und der Datenschutzerklärung von Apple.
      </LegalP>

      <LegalH2>6. Google Login</LegalH2>
      <LegalP>
        Bei der Registrierung über «Anmelden mit Google» erhalten wir von Google
        die zur Kontoerstellung nötigen Angaben (z. B. eine Kennung und
        E-Mail-Adresse). Die Anmeldung erfolgt nach den Vorgaben und der
        Datenschutzerklärung von Google.
      </LegalP>

      <LegalH2>7. Haushalte und Haushaltsmitglieder</LegalH2>
      <LegalP>
        Mit «WasGitsHüt?» kannst du einen Haushalt erstellen und weitere
        Personen als Mitglieder einladen und verwalten. Innerhalb eines
        Haushalts werden bestimmte Angaben mit den anderen Mitgliedern geteilt –
        insbesondere gemeinsame Rezepte, Abstimmungen und Einkaufslisten. Deine
        persönlichen Essenspräferenzen sind hingegen ausschliesslich für dich
        selbst sowie für die Administration deines Kontos einsehbar und werden
        nicht mit anderen Haushaltsmitgliedern geteilt. Da mehrere Personen
        Mitglied desselben Haushalts sein können, solltest du nur Angaben teilen,
        die für die anderen Mitglieder bestimmt sind. Du kannst einen Haushalt
        jederzeit verlassen.
      </LegalP>

      <LegalH2>8. Allergien, Unverträglichkeiten und Ernährungspräferenzen</LegalH2>
      <LegalP>
        Du kannst Allergien, Unverträglichkeiten und ernährungsbezogene Angaben
        freiwillig hinterlegen, damit dir passendere Rezepte vorgeschlagen
        werden. Angaben zu Allergien und Unverträglichkeiten können Rückschlüsse
        auf die Gesundheit zulassen. Solche Daten können nach Schweizer DSG als
        «besonders schützenswerte Personendaten» und nach DSGVO als «besondere
        Kategorien personenbezogener Daten» (Gesundheitsdaten, Art. 9 DSGVO)
        gelten.
      </LegalP>
      <LegalP>
        Wir bearbeiten diese Angaben ausschliesslich auf Grundlage deiner
        ausdrücklichen Einwilligung, die du mit der Eingabe im Profil erteilst,
        und nur zum Zweck der Rezeptfindung. Du kannst diese Einwilligung
        jederzeit widerrufen und die Angaben löschen. Die Eingabe dieser Daten
        ist freiwillig; ohne sie stehen dir die übrigen Funktionen weiterhin
        offen.
      </LegalP>

      <LegalH2>9. Rezeptgenerierung und künstliche Intelligenz</LegalH2>
      <LegalP>
        Für die Erstellung von Rezeptvorschlägen sowie von Rezepttexten und
        Rezeptbildern setzen wir Funktionen ein, die auf künstlicher Intelligenz
        beruhen. Dabei werden die für den Vorschlag nötigen Eingaben (z. B.
        vorhandene Zutaten, gewünschte Küche, Ernährungsangaben) an externe
        Technologiedienstleister übermittelt (siehe Kapitel 16). Es werden nur
        die Angaben übertragen, die zur Erstellung des gewünschten Ergebnisses
        erforderlich sind.
      </LegalP>

      <LegalH2>10. Speicherung von Rezepten, Favoriten und Einkaufslisten</LegalH2>
      <LegalP>
        Damit du deine Ergebnisse wiederfindest, speichern wir favorisierte
        Rezepte, Abstimmungsergebnisse und erstellte Einkaufslisten. Innerhalb
        eines Haushalts können diese Inhalte mit den anderen Mitgliedern geteilt
        werden. Rechtsgrundlage ist die Erfüllung des Nutzungsvertrags (nach
        DSGVO Art. 6 Abs. 1 lit. b).
      </LegalP>

      <LegalH2>11. Abonnemente und Zahlungen</LegalH2>
      <LegalP>
        «WasGitsHüt?» kann kostenpflichtige Abonnemente und In-App-Käufe
        anbieten. Die Zahlung wird über den Apple App Store oder Google Play
        abgewickelt. Wir erhalten und speichern keine Kreditkarten- oder
        vollständigen Zahlungsdaten. Wir erhalten lediglich die zur Verwaltung
        des Abonnements nötigen Informationen (z. B. Abo-Status, eine
        Transaktions- bzw. Bestellkennung). Allfällige Preise werden in CHF
        angegeben.
      </LegalP>

      <LegalH2>12. RevenueCat</LegalH2>
      <LegalP>
        Für die Verwaltung von Abonnementen und In-App-Käufen können wir den
        Dienst RevenueCat einsetzen. RevenueCat bearbeitet in unserem Auftrag
        Angaben rund um Käufe und Abonnemente (z. B. Kauf- und Abo-Status sowie
        vom Store vergebene Kennungen). Die Bearbeitung erfolgt nach der
        Datenschutzerklärung von RevenueCat.
      </LegalP>

      <LegalH2>13. Supabase</LegalH2>
      <LegalP>
        Für Backend, Datenbank, Authentifizierung und weitere Backend-Funktionen
        können wir den Dienst Supabase einsetzen. Supabase bearbeitet die in der
        App gespeicherten Daten in unserem Auftrag als Auftragsbearbeiter.
      </LegalP>

      <LegalH2>14. Apple App Store</LegalH2>
      <LegalP>
        Wenn du die App über den Apple App Store beziehst und dort Käufe tätigst,
        bearbeitet Apple die damit verbundenen Daten (z. B. Kauf- und
        Zahlungsabwicklung) als eigenverantwortliche Stelle nach der
        Datenschutzerklärung von Apple.
      </LegalP>

      <LegalH2>15. Google Play Store</LegalH2>
      <LegalP>
        Wenn du die App über Google Play beziehst und dort Käufe tätigst,
        bearbeitet Google die damit verbundenen Daten (z. B. Kauf- und
        Zahlungsabwicklung) als eigenverantwortliche Stelle nach der
        Datenschutzerklärung von Google.
      </LegalP>

      <LegalH2>16. Einsatz von künstlicher Intelligenz</LegalH2>
      <LegalP>
        Für die Generierung von Rezepttexten und Rezeptbildern sowie für
        einzelne Funktionen wie die Sprachverarbeitung setzen wir spezialisierte
        externe Technologiedienstleister ein. An diese werden ausschliesslich
        die für die jeweilige Funktion nötigen Eingaben übermittelt. Wir wählen
        dafür Anbieter, die einen angemessenen Datenschutz gewährleisten, und
        übermitteln nicht mehr Angaben als erforderlich. Die konkret
        eingesetzten Anbieter und Modelle nennen wir aus Wettbewerbs- und
        Geheimhaltungsgründen nicht namentlich.
      </LegalP>

      <LegalH2>17. Weitergabe von Daten an Dritte und Auftragsbearbeiter</LegalH2>
      <LegalP>
        Wir geben Personendaten nur weiter, soweit dies für die beschriebenen
        Zwecke erforderlich ist. Als Auftragsbearbeiter bzw. Dienstleister können
        insbesondere unsere Backend- und Technologiedienstleister tätig werden.
        Apple und Google handeln im Rahmen von Login, Store und Zahlungen als
        eigenverantwortliche Stellen. Wir verkaufen deine Personendaten nicht und
        geben sie nicht für werbliche Zwecke an Dritte weiter.
      </LegalP>

      <LegalH2>18. Bearbeitung und Speicherung von Daten im Ausland</LegalH2>
      <LegalP>
        Die in der App erfassten Daten werden auf Servern in Frankfurt
        (Deutschland) und damit innerhalb der EU gespeichert. Deutschland verfügt
        über ein angemessenes Datenschutzniveau; die Bearbeitung erfolgt im
        Einklang mit dem Schweizer DSG und der EU-DSGVO. Soweit einzelne
        Dienstleister (z. B. für Login, Stores oder einzelne Funktionen) Daten in
        weiteren Ländern bearbeiten, stützen wir uns auf geeignete rechtliche
        Garantien wie Standardvertragsklauseln.
      </LegalP>

      <LegalH2>19. Datensicherheit</LegalH2>
      <LegalP>
        Wir treffen angemessene technische und organisatorische Massnahmen, um
        deine Daten zu schützen – insbesondere Verschlüsselung bei der
        Übertragung, Zugriffsbeschränkungen und die Trennung von Haushaltsdaten.
        Für die Datenhaltung setzen wir auf einen zertifizierten Datenbank- und
        Hosting-Anbieter (MongoDB Atlas), der unter anderem nach den folgenden
        Standards zertifiziert ist:
      </LegalP>
      <LegalUl
        items={[
          "ISO/IEC 27001:2022 (Informationssicherheits-Managementsystem)",
          "ISO/IEC 27017:2015 (Informationssicherheit für Cloud-Dienste)",
          "ISO/IEC 27018:2019 (Schutz personenbezogener Daten in der Cloud)",
          "ISO 9001:2015 (Qualitätsmanagement)",
          "SOC 2 Type II"
        ]}
      />

      <LegalH2>20. Speicherdauer und Löschung von Daten</LegalH2>
      <LegalP>
        Wir bearbeiten Personendaten nur so lange, wie es für den jeweiligen
        Zweck erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
      </LegalP>

      <LegalH2>21. Löschung eines Benutzerkontos</LegalH2>
      <LegalP>
        Du kannst die Löschung deines Benutzerkontos und der damit verbundenen
        Daten jederzeit verlangen. Nach der Löschung werden deine
        personenbezogenen Angaben entfernt, soweit keine gesetzlichen
        Aufbewahrungspflichten entgegenstehen. Bereits mit einem Haushalt
        geteilte Inhalte können bei den übrigen Mitgliedern bestehen bleiben, in
        anonymisierter bzw. von dir gelöster Form. Die Löschung eines Kontos
        beendet ein laufendes Store-Abonnement nicht automatisch – dieses musst
        du über Apple bzw. Google kündigen. Die Löschung verlangst du per E-Mail
        (eine Löschung direkt in der App ist nicht vorgesehen):
      </LegalP>
      <LegalUl
        items={[
          <>Per E-Mail an: <Mail /></>
        ]}
      />

      <LegalH2>22. Rechte der betroffenen Personen nach Schweizer DSG</LegalH2>
      <LegalP>
        Nach dem Schweizer DSG hast du insbesondere folgende Rechte:
      </LegalP>
      <LegalUl
        items={[
          "Recht auf Auskunft über die zu deiner Person bearbeiteten Daten",
          "Recht auf Berichtigung unrichtiger Daten",
          "Recht auf Löschung bzw. Vernichtung von Daten",
          "Recht auf Herausgabe oder Übertragung deiner Daten",
          "Recht, einer bestimmten Datenbearbeitung zu widersprechen"
        ]}
      />
      <LegalP>
        Die Löschung deiner Daten und deines Kontos kannst du per E-Mail an{" "}
        <Mail /> verlangen.
      </LegalP>

      <LegalH2>23. Zusätzliche Rechte für Personen, für welche die DSGVO gilt</LegalH2>
      <LegalP>
        Fällst du unter die DSGVO, hast du zusätzlich folgende Rechte:
      </LegalP>
      <LegalUl
        items={[
          "Recht auf Einschränkung der Bearbeitung",
          "Recht auf Datenübertragbarkeit in einem gängigen Format",
          "Recht auf Widerspruch gegen bestimmte Bearbeitungen",
          "Recht auf Widerruf einer erteilten Einwilligung (mit Wirkung für die Zukunft)",
          "Recht auf Beschwerde bei einer Datenschutz-Aufsichtsbehörde"
        ]}
      />
      <LegalP>
        Zur Ausübung deiner Rechte kontaktiere uns unter <Mail />. In der Schweiz
        ist der Eidgenössische Datenschutz- und Öffentlichkeitsbeauftragte (EDÖB)
        zuständig; in der EU die jeweilige nationale Aufsichtsbehörde.
      </LegalP>

      <LegalH2>24. Minderjährige und Nutzung innerhalb eines Familienhaushalts</LegalH2>
      <LegalP>
        «WasGitsHüt?» wird im Familienumfeld genutzt. Ein Alter der nutzenden
        Person wird für die Nutzung weder benötigt noch erhoben oder verlangt –
        dies gilt für Kinder wie für Erwachsene gleichermassen. Werden Kinder als
        Haushaltsmitglieder erfasst, erfolgt dies durch einen Elternteil oder eine
        erziehungsberechtigte Person; die dafür erforderliche Einwilligung wird
        mit dem eigenständigen Erfassen des Kindes durch diese Person erteilt.
        Daten von Kindern werden nicht für Werbe- oder Profilbildungszwecke
        verwendet.
      </LegalP>

      <LegalH2>25. Cookies bzw. Webseitentechnologien</LegalH2>
      <LegalP>
        Auf unserer Webseite können technisch notwendige sowie – mit deiner
        Einwilligung – weitere Technologien (z. B. für Statistik) zum Einsatz
        kommen.
      </LegalP>

      <LegalH2>26. Änderungen dieser Datenschutzerklärung</LegalH2>
      <LegalP>
        Wir können diese Datenschutzerklärung anpassen, um sie an geänderte
        Abläufe oder rechtliche Vorgaben anzupassen. Massgebend ist die jeweils
        auf dieser Seite veröffentlichte Fassung. Bei wesentlichen Änderungen
        informieren wir dich in geeigneter Form.
      </LegalP>

      <LegalH2>27. Kontakt</LegalH2>
      <LegalP>
        Bei Fragen zum Datenschutz erreichst du uns unter:
      </LegalP>
      <LegalUl
        items={[
          "Liberox GmbH, Bodenäckerstrasse 3, 8957 Spreitenbach, Schweiz",
          "Telefon: 056 561 67 62",
          <>E-Mail: <Mail /></>
        ]}
      />
    </LegalLayout>
  );
}
